/**
 * @fileoverview Delivery Proof Management Hooks
 * 
 * Provides functionality for vendors to submit proof of delivery and for clients
 * to review and accept/reject deliveries. Delivery proofs are the final step in
 * the job workflow before payment release and job completion.
 * 
 * Delivery Proof Workflow:
 * 1. Vendor completes job and prepares delivery
 * 2. Vendor submits delivery proof with photos, receipts, signatures
 * 3. Client receives notification to review delivery
 * 4. Client reviews proof and accepts, rejects, or requests clarification
 * 5. If accepted: Payment released, job marked complete
 * 6. If rejected: Vendor can resubmit (limited attempts)
 * 7. Auto-acceptance after 15 business days if no review
 * 
 * Review Statuses:
 * - pending: Awaiting client review (initial state)
 * - accepted: Client approved delivery
 * - rejected: Client rejected delivery (vendor can resubmit)
 * - needs_clarification: Client needs more information
 * 
 * Resubmission Rules:
 * - Maximum 2 resubmissions allowed per delivery proof
 * - Each resubmission resets 15 business day auto-accept timer
 * - Resubmission count tracked for compliance
 * 
 * Auto-Acceptance:
 * - Delivery proofs auto-accept after 15 business days if no client review
 * - Prevents indefinite payment delays
 * - Timer resets on resubmission
 * 
 * Delivery Exceptions:
 * - Track issues like damaged goods, missing items, late delivery
 * - Exceptions linked to delivery proofs
 * - Severity levels: low, medium, high, critical
 * - Resolution tracking and notes
 * 
 * Key Features:
 * - Submit delivery proof with comprehensive details
 * - Client review workflow with accept/reject/clarify
 * - Photo and document upload support
 * - Signature capture for recipient and driver
 * - Delivery exception tracking
 * - Resubmission with limits
 * - Auto-acceptance after timeout
 * - Job status synchronization
 * 
 * @module useDeliveryProofs
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { DeliveryProof, DeliveryException } from '@/lib/types/delivery';

interface UseDeliveryProofsOptions {
  jobId?: string;
  organizationId?: string;
}

/**
 * Hook for managing multiple delivery proofs
 * 
 * Fetches and manages delivery proofs filtered by job or organization.
 * Includes submission, review, exception tracking, and resubmission functionality.
 * 
 * Data Loading:
 * - Fetches delivery proofs with related exceptions
 * - Filters by job ID or organization ID if provided
 * - Orders by creation date (most recent first)
 * - Automatically refetches after mutations
 * 
 * @param options - Optional filters for job or organization
 * @returns Object with delivery proofs data and management functions
 * 
 * @example
 * ```tsx
 * function DeliveryProofsList({ jobId }) {
 *   const {
 *     deliveryProofs,
 *     loading,
 *     submitDeliveryProof,
 *     reviewDeliveryProof
 *   } = useDeliveryProofs({ jobId });
 * 
 *   if (loading) return <Spinner />;
 * 
 *   return (
 *     <div>
 *       {deliveryProofs.map(proof => (
 *         <DeliveryProofCard
 *           key={proof.id}
 *           proof={proof}
 *           onReview={(status, notes) => 
 *             reviewDeliveryProof(proof.id, { status, notes })
 *           }
 *         />
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export const useDeliveryProofs = (options: UseDeliveryProofsOptions = {}) => {
  const [deliveryProofs, setDeliveryProofs] = useState<DeliveryProof[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetches delivery proofs with optional filters
   * 
   * Includes related delivery exceptions in the query for comprehensive
   * delivery status information.
   */
  const fetchDeliveryProofs = async () => {
    try {
      setLoading(true);
      
      let query = supabase
        .from('delivery_proofs')
        .select(`
          *,
          delivery_exceptions (*)
        `)
        .order('created_at', { ascending: false });

      if (options.jobId) {
        query = query.eq('job_id', options.jobId);
      }

      if (options.organizationId) {
        query = query.eq('organization_id', options.organizationId);
      }

      const { data, error } = await query;

      if (error) throw error;
      
      setDeliveryProofs(data as any as DeliveryProof[]);
      setError(null);
    } catch (err) {
      console.error('Error fetching delivery proofs:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch delivery proofs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDeliveryProofs();
  }, [options.jobId, options.organizationId]);

  /**
   * Submits a new delivery proof for client review
   * 
   * Vendors submit delivery proofs when they complete job delivery. Proof
   * includes delivery details, photos, receipts, and signatures. Submission
   * updates job status to completed_pending_review.
   * 
   * Auto-Acceptance:
   * Sets auto_accept_at to 15 business days (~21 calendar days) from now. If client doesn't review within
   * 15 business days, the delivery is automatically accepted and payment released.
   * 
   * Required Information:
   * - Delivery date and time
   * - Delivery location details
   * - Items delivered (list with quantities)
   * - Condition on delivery (good, damaged, etc.)
   * - Delivery photos (at least 1 recommended)
   * - Recipient signature
   * 
   * Optional Information:
   * - Carrier information and tracking
   * - Packing slip URL
   * - Invoice reference
   * - Installation notes and photos
   * - Warranty activation status
   * - Training provided status
   * 
   * @param deliveryData - Comprehensive delivery information
   * @returns Created delivery proof record
   * @throws Error if submission fails or user not authenticated
   * 
   * @example
   * ```typescript
   * const proof = await submitDeliveryProof({
   *   job_id: 'job-uuid',
   *   purchase_order_id: 'po-uuid',
   *   delivery_date: '2024-01-15',
   *   delivery_time: '14:30',
   *   delivery_method: 'ground_shipping',
   *   delivery_location: {
   *     address: '123 Main St',
   *     city: 'Portland',
   *     state: 'OR',
   *     zip: '97201'
   *   },
   *   carrier_info: {
   *     name: 'FedEx',
   *     tracking: '1234567890'
   *   },
   *   items_delivered: [
   *     { sku: 'PART-001', quantity: 100, condition: 'good' }
   *   ],
   *   partial_delivery: false,
   *   condition_on_delivery: 'good',
   *   delivery_notes: 'All items delivered in excellent condition',
   *   delivery_photos: [
   *     { url: 'https://...', caption: 'Package exterior' },
   *     { url: 'https://...', caption: 'Items unpacked' }
   *   ],
   *   recipient_signature: {
   *     name: 'John Doe',
   *     signature_url: 'https://...',
   *     timestamp: '2024-01-15T14:35:00Z'
   *   }
   * });
   * ```
   */
  const submitDeliveryProof = async (deliveryData: {
    job_id: string;
    purchase_order_id?: string;
    delivery_date: string;
    delivery_time?: string;
    delivery_method: string;
    delivery_location: any;
    carrier_info?: any;
    items_delivered: any[];
    partial_delivery: boolean;
    condition_on_delivery: string;
    delivery_notes?: string;
    exceptions_noted?: string;
    delivery_photos: any[];
    delivery_receipts?: string[];
    packing_slip_url?: string;
    invoice_reference?: string;
    recipient_signature?: any;
    driver_signature?: any;
    installation_notes?: string;
    installation_photos?: string[];
    warranty_activation?: boolean;
    training_provided?: boolean;
  }) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: profile } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('user_id', userData.user.id)
        .single();

      if (!profile) throw new Error('User profile not found');

      // Calculate auto-accept date (~21 calendar days for 15 business days)
      const autoAcceptAt = new Date();
      autoAcceptAt.setDate(autoAcceptAt.getDate() + 21);

      const { data, error } = await supabase
        .from('delivery_proofs')
        .insert({
          ...deliveryData,
          organization_id: profile.organization_id,
          submitted_by_user_id: userData.user.id,
          client_review_status: 'pending',
          auto_accept_at: autoAcceptAt.toISOString(),
          resubmission_count: 0,
          max_resubmissions: 2,
          recipient_name: deliveryData.delivery_location?.contact_name || 'Unknown',
        } as any)
        .select()
        .single();

      if (error) throw error;

      // Update job status to completed_pending_review
      await supabase
        .from('jobs')
        .update({ status: 'completed_pending_review' })
        .eq('id', deliveryData.job_id);

      await fetchDeliveryProofs();
      return data;
    } catch (err) {
      console.error('Error submitting delivery proof:', err);
      throw err;
    }
  };

  /**
   * Reviews a delivery proof (client action)
   * 
   * Clients review delivery proofs submitted by vendors and can accept,
   * reject, or request clarification. Review updates job status accordingly.
   * 
   * Review Actions:
   * - accepted: Approve delivery, release payment, complete job
   * - rejected: Reject delivery, vendor can resubmit with corrections
   * - needs_clarification: Request more information without rejecting
   * 
   * Job Status Updates:
   * - accepted → job status: 'accepted'
   * - rejected → job status: 'rejected'
   * - needs_clarification → job status: 'completed_pending_review' (stays in review)
   * 
   * @param deliveryProofId - ID of delivery proof to review
   * @param reviewData - Review status and optional notes
   * @throws Error if review fails or user not authenticated
   * 
   * @example
   * ```typescript
   * // Accept delivery
   * await reviewDeliveryProof('proof-uuid', {
   *   status: 'accepted',
   *   notes: 'All items received in good condition'
   * });
   * 
   * // Reject delivery
   * await reviewDeliveryProof('proof-uuid', {
   *   status: 'rejected',
   *   notes: 'Missing 5 units, please redeliver'
   * });
   * 
   * // Request clarification
   * await reviewDeliveryProof('proof-uuid', {
   *   status: 'needs_clarification',
   *   notes: 'Please provide clearer photos of serial numbers'
   * });
   * ```
   */
  const reviewDeliveryProof = async (
    deliveryProofId: string,
    reviewData: {
      status: 'accepted' | 'rejected' | 'needs_clarification';
      notes?: string;
    }
  ) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { error } = await supabase
        .from('delivery_proofs')
        .update({
          client_review_status: reviewData.status,
          client_reviewed_at: new Date().toISOString(),
          client_reviewed_by_user_id: userData.user.id,
          client_review_notes: reviewData.notes,
        })
        .eq('id', deliveryProofId);

      if (error) throw error;

      // Update job status based on review result
      const deliveryProof = deliveryProofs.find(dp => dp.id === deliveryProofId);
      if (deliveryProof) {
        let jobStatus = 'in_progress'; // Default fallback
        
        if (reviewData.status === 'accepted') {
          jobStatus = 'accepted';
        } else if (reviewData.status === 'rejected') {
          jobStatus = 'rejected';
        } else if (reviewData.status === 'needs_clarification') {
          jobStatus = 'completed_pending_review'; // Keep in review until clarified
        }

        await supabase
          .from('jobs')
          .update({ status: jobStatus })
          .eq('id', deliveryProof.job_id);
      }

      await fetchDeliveryProofs();
    } catch (err) {
      console.error('Error reviewing delivery proof:', err);
      throw err;
    }
  };

  /**
   * Creates a delivery exception
   * 
   * Delivery exceptions track issues like damaged goods, missing items,
   * late delivery, or other problems. Exceptions can be created by either
   * party and require resolution.
   * 
   * Exception Types:
   * - damaged_goods: Items arrived damaged
   * - missing_items: Items missing from delivery
   * - late_delivery: Delivery past agreed date
   * - wrong_items: Incorrect items delivered
   * - quantity_discrepancy: Quantity doesn't match order
   * - quality_issue: Items don't meet quality standards
   * - other: Other issues
   * 
   * Severity Levels:
   * - low: Minor issue, doesn't block acceptance
   * - medium: Moderate issue, may require follow-up
   * - high: Significant issue, requires resolution before acceptance
   * - critical: Severe issue, blocks acceptance completely
   * 
   * @param exceptionData - Exception details
   * @returns Created exception record
   * @throws Error if creation fails
   * 
   * @example
   * ```typescript
   * await createDeliveryException({
   *   delivery_proof_id: 'proof-uuid',
   *   exception_type: 'damaged_goods',
   *   severity: 'high',
   *   description: '3 units have visible dents and scratches on surface',
   *   photos: ['https://...photo1.jpg', 'https://...photo2.jpg'],
   *   resolution_required: true
   * });
   * ```
   */
  const createDeliveryException = async (exceptionData: {
    delivery_proof_id: string;
    exception_type: string;
    severity: string;
    description: string;
    photos?: string[];
    resolution_required?: boolean;
  }) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('delivery_exceptions')
        .insert({
          ...exceptionData,
          created_by_user_id: userData.user.id,
          resolution_required: exceptionData.resolution_required ?? true,
          photos: exceptionData.photos || [],
        })
        .select()
        .single();

      if (error) throw error;

      await fetchDeliveryProofs();
      return data;
    } catch (err) {
      console.error('Error creating delivery exception:', err);
      throw err;
    }
  };

  /**
   * Resubmits a rejected or clarification-requested delivery proof
   * 
   * Vendors can resubmit delivery proofs up to the max_resubmissions limit
   * (default 2). Resubmission resets the auto-accept timer and client review
   * status, allowing for a fresh review.
   * 
   * Resubmission Process:
   * 1. Check resubmission limit not exceeded
   * 2. Update delivery proof with corrections
   * 3. Increment resubmission_count
   * 4. Reset auto_accept_at to 15 business days (~21 calendar days) from now
   * 5. Reset review status to pending
   * 6. Update job status back to completed_pending_review
   * 
   * @param deliveryProofId - ID of proof to resubmit
   * @param updates - Updated delivery information
   * @throws Error if resubmission limit reached or update fails
   * 
   * @example
   * ```typescript
   * await resubmitDeliveryProof('proof-uuid', {
   *   delivery_notes: 'Re-delivered missing 5 units',
   *   delivery_photos: [
   *     { url: 'https://...', caption: 'Complete delivery with all units' }
   *   ],
   *   exceptions_noted: 'Missing units issue resolved'
   * });
   * ```
   */
  const resubmitDeliveryProof = async (
    deliveryProofId: string,
    updates: {
      delivery_notes?: string;
      exceptions_noted?: string;
      delivery_photos?: any[];
      installation_notes?: string;
    }
  ) => {
    try {
      const deliveryProof = deliveryProofs.find(dp => dp.id === deliveryProofId);
      if (!deliveryProof) throw new Error('Delivery proof not found');
      
      if (deliveryProof.resubmission_count >= deliveryProof.max_resubmissions) {
        throw new Error('Resubmission limit reached');
      }

      // Reset auto-accept date (~21 calendar days for 15 business days)
      const newAutoAcceptAt = new Date();
      newAutoAcceptAt.setDate(newAutoAcceptAt.getDate() + 21);

      const { error } = await supabase
        .from('delivery_proofs')
        .update({
          ...updates,
          resubmission_count: deliveryProof.resubmission_count + 1,
          auto_accept_at: newAutoAcceptAt.toISOString(),
          client_review_status: 'pending',
          client_reviewed_at: null,
          client_reviewed_by_user_id: null,
          client_review_notes: null,
        } as any)
        .eq('id', deliveryProofId);

      if (error) throw error;

      // Update job status back to completed_pending_review
      await supabase
        .from('jobs')
        .update({ status: 'completed_pending_review' })
        .eq('id', deliveryProof.job_id);

      await fetchDeliveryProofs();
    } catch (err) {
      console.error('Error resubmitting delivery proof:', err);
      throw err;
    }
  };

  return {
    deliveryProofs,
    loading,
    error,
    refetch: fetchDeliveryProofs,
    submitDeliveryProof,
    reviewDeliveryProof,
    createDeliveryException,
    resubmitDeliveryProof,
  };
};

/**
 * Hook for managing a single delivery proof
 * 
 * Simplified hook for working with one specific delivery proof. Fetches
 * the proof with related exceptions and provides refetch capability.
 * 
 * @param deliveryProofId - ID of the delivery proof to fetch
 * @returns Object with delivery proof data, loading state, and refetch function
 * 
 * @example
 * ```tsx
 * function DeliveryProofDetails({ proofId }) {
 *   const { deliveryProof, loading, refetch } = useDeliveryProof(proofId);
 * 
 *   if (loading) return <Spinner />;
 *   if (!deliveryProof) return <p>Proof not found</p>;
 * 
 *   return (
 *     <div>
 *       <h2>Delivery Proof {deliveryProof.public_id}</h2>
 *       <p>Status: {deliveryProof.client_review_status}</p>
 *       <DeliveryDetails proof={deliveryProof} />
 *       {deliveryProof.delivery_exceptions?.map(ex => (
 *         <ExceptionCard key={ex.id} exception={ex} />
 *       ))}
 *       <Button onClick={refetch}>Refresh</Button>
 *     </div>
 *   );
 * }
 * ```
 */
export const useDeliveryProof = (deliveryProofId: string) => {
  const [deliveryProof, setDeliveryProof] = useState<DeliveryProof | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDeliveryProof = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('delivery_proofs')
        .select(`
          *,
          delivery_exceptions (*)
        `)
        .eq('id', deliveryProofId)
        .single();

      if (error) throw error;
      
      setDeliveryProof(data as any as DeliveryProof);
      setError(null);
    } catch (err) {
      console.error('Error fetching delivery proof:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch delivery proof');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (deliveryProofId) {
      fetchDeliveryProof();
    }
  }, [deliveryProofId]);

  return {
    deliveryProof,
    loading,
    error,
    refetch: fetchDeliveryProof,
  };
};